// src/utils/api.js
/**
 * API 工具函数
 */

const BASE_URL = '';

// 通用请求方法
const request = async (url, options = {}) => {
  try {
    const response = await fetch(`${BASE_URL}${url}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });
    return await response.json();
  } catch (error) {
    console.error(`请求 ${url} 失败:`, error);
    throw error;
  }
};

/**
 * 获取代理人信息
 */
export const apiGetAgent = async (id) => {
  return await request(`/api/agents/${id}`);
};

/**
 * 通过用户openid获取代理信息
 */
export const apiGetUserAgentByOpenId = async (openid) => {
  return await request(`/api/users/${openid}/agent`);
};

/**
 * 获取代理人统计数据
 */
export const apiGetAgentStats = async (id) => {
  return await request(`/api/agents/${id}/stats`);
};

/**
 * 获取代理人今日统计数据
 */
export const apiGetAgentTodayStats = async (id) => {
  return await request(`/api/agents/${id}/today-stats`);
};

/**
 * 获取代理人历史数据
 */
export const apiGetAgentHistoryData = async (id) => {
  return await request(`/api/agents/${id}/history-data`);
};

/**
 * 获取代理人提现记录
 */
export const apiGetAgentWithdrawals = async (id) => {
  return await request(`/api/agents/${id}/withdrawals`);
};

/**
 * 代理人申请提现
 */
export const apiAgentWithdraw = async (id, amount) => {
  return await request(`/api/agents/${id}/withdraw`, {
    method: 'POST',
    body: JSON.stringify({ amount })
  });
};

/**
 * 保存代理人个人信息
 */
export const apiSaveAgentPersonalInfo = async (id, info) => {
  return await request(`/api/agents/${id}/personal-info`, {
    method: 'POST',
    body: JSON.stringify(info)
  });
};

/**
 * 获取用户信息
 */
export const apiGetUserInfo = async (userId) => {
  return await request(`/api/user/${userId}`);
};

/**
 * 更新用户信息
 */
export const apiUpdateUserInfo = async (userId, data) => {
  return await request(`/api/user/update`, {
    method: 'POST',
    body: JSON.stringify({ userId, ...data })
  });
};

/**
 * 获取用户订单列表
 */
export const apiGetUserOrders = async (userId) => {
  return await request(`/api/orders/user/${userId}`);
};

/**
 * 创建图片/视频生成订单
 */
export const apiCreateOrder = async (orderData) => {
  return await request('/api/order/create', {
    method: 'POST',
    body: JSON.stringify(orderData)
  });
};

/**
 * 创建图片生成订单（支持文件上传）
 */
export const apiCreateImageOrder = async (orderData, imageFiles) => {
  const formData = new FormData();

  // 添加订单数据
  Object.keys(orderData).forEach(key => {
    formData.append(key, orderData[key]);
  });

  // 添加图片文件
  if (imageFiles && imageFiles.length > 0) {
    imageFiles.forEach((file) => {
      formData.append('images', file);
    });
  }

  const response = await fetch(`${BASE_URL}/api/order/create`, {
    method: 'POST',
    body: formData
  });

  return await response.json();
};

/**
 * 预上传参考图（临时保存）
 */
export const apiUploadTempImages = async (imageFiles) => {
  const formData = new FormData();

  if (imageFiles && imageFiles.length > 0) {
    imageFiles.forEach((file) => {
      formData.append('images', file);
    });
  }

  const response = await fetch(`${BASE_URL}/api/upload/temp`, {
    method: 'POST',
    body: formData
  });

  return await response.json();
};

/**
 * 确认使用临时图片（支付成功后）
 */
export const apiConfirmTempImages = async (tempPaths, orderId) => {
  return await request('/api/upload/confirm', {
    method: 'POST',
    body: JSON.stringify({ tempPaths, orderId })
  });
};

/**
 * 删除临时图片（支付取消/失败）
 */
export const apiCancelTempImages = async (tempPaths) => {
  return await request('/api/upload/cancel', {
    method: 'POST',
    body: JSON.stringify({ tempPaths })
  });
};

/**
 * 调用微信支付
 */
export const callWechatPay = (paymentParams) => {
  return new Promise((resolve, reject) => {
    if (typeof WeixinJSBridge === 'undefined') {
      reject(new Error('请在微信中打开页面进行支付'));
      return;
    }

    WeixinJSBridge.invoke(
      'getBrandWCPayRequest',
      paymentParams,
      function(res) {
        if (res.err_msg === "get_brand_wcpay_request:ok") {
          resolve({ success: true });
        } else {
          reject(new Error('支付失败或已取消'));
        }
      }
    );
  });
};

/**
 * 发起微信支付（创建订单并调起支付）
 */
export const apiInitiatePayment = async (orderData) => {
  try {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    const fullOrderData = {
      ...orderData,
      userId: orderData.userId || (user ? user.id : null)
    };

    const response = await apiCreateOrder(fullOrderData);

    if (response.success) {
      // 调试模式直接返回成功
      if (!response.payParams) {
        return { success: true, debug: true };
      }
      // 正式模式调起微信支付
      await callWechatPay(response.payParams);
      return { success: true };
    } else {
      throw new Error(response.error || '创建订单失败');
    }
  } catch (error) {
    console.error('支付失败:', error);
    throw error;
  }
};

export default {
  apiGetAgent,
  apiGetUserAgentByOpenId,
  apiGetAgentStats,
  apiGetAgentTodayStats,
  apiGetAgentHistoryData,
  apiGetAgentWithdrawals,
  apiAgentWithdraw,
  apiSaveAgentPersonalInfo,
  apiGetUserInfo,
  apiUpdateUserInfo,
  apiGetUserOrders,
  apiCreateOrder,
  callWechatPay,
  apiInitiatePayment
};
